import type { KeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { Link } from 'react-router-dom';
import { Scissors, Lock, GripVertical } from 'lucide-react';

interface CutLineControlProps {
  position: number;
  totalItems: number;
  onMove: (newPosition: number) => void;
  canMove: boolean;
  /**
   * S-20: when a session produced this pool, its line is set there. The
   * handle is not drawn; this line says where the line is set instead.
   */
  setBySession?: { id: string; name: string } | null;
  /**
   * Drag the handle up or down the list. The parent owns the pointer math
   * (it knows where the rows are) and commits once on release; while the
   * pointer is down it passes the position the line would land on.
   */
  onDragStart?: (e: ReactPointerEvent<HTMLButtonElement>) => void;
  draftPosition?: number | null;
}

/**
 * The cut line: a dashed coral rule across the list with one handle on it.
 * Drag the handle to where the line should sit, or focus it and use the
 * arrow keys (Home and End take it to the top or bottom). One handle, one
 * act; the counts on it follow the pointer while it is dragged (owner,
 * 2026-10-15: "drag it up and down, not click it one thing at a time").
 */
export function CutLineControl({ position, totalItems, onMove, canMove: canMoveProp, setBySession, onDragStart, draftPosition }: CutLineControlProps) {
  const canMove = canMoveProp && !setBySession;
  const shown = draftPosition ?? position;
  const aboveCount = shown;
  const belowCount = totalItems - shown;
  const dragging = draftPosition != null;

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!canMove) return;
    let next: number | null = null;
    if (e.key === 'ArrowUp') next = Math.max(0, position - 1);
    else if (e.key === 'ArrowDown') next = Math.min(totalItems, position + 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = totalItems;
    if (next === null) return;
    e.preventDefault();
    if (next !== position) onMove(next);
  };

  return (
    <div className="relative my-3" data-testid="cut-line">
      <div className="relative flex items-center">
        <div className="flex-1 border-t-4 border-dashed border-[var(--coral)]" />

        {canMove ? (
          <button
            type="button"
            aria-label={`Cut line: ${aboveCount} above, ${belowCount} below. Drag it, or use the arrow keys, to move it.`}
            title="Drag to move the cut line"
            onPointerDown={onDragStart}
            onKeyDown={onKeyDown}
            className={`flex touch-none select-none items-center gap-2 rounded-full border-2 border-[var(--coral)] bg-[var(--coral-dim)] px-3 py-1.5 text-[var(--coral)] ${
              dragging ? 'cursor-grabbing shadow-md' : 'cursor-grab'
            } focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)] focus-visible:ring-offset-2`}
          >
            <GripVertical className="h-4 w-4 opacity-70" aria-hidden />
            <Scissors className="h-4 w-4" aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-wider">Cut line</span>
            <span className="text-xs font-medium tabular-nums">
              {aboveCount} above · {belowCount} below
            </span>
          </button>
        ) : (
          <div className="flex items-center gap-2 rounded-full border-2 border-[var(--rule)] bg-[var(--hover-tint)] px-3 py-1.5 text-[var(--text-ter)]">
            <Lock className="h-4 w-4" aria-hidden />
            <Scissors className="h-4 w-4" aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-wider">Cut line</span>
            <span className="text-xs font-medium tabular-nums">
              {aboveCount} above · {belowCount} below
            </span>
          </div>
        )}

        <div className="flex-1 border-t-4 border-dashed border-[var(--coral)]" />
      </div>

      {setBySession && (
        <p className="mt-2 text-xs text-[var(--text-sec)]">
          Set by the session{' '}
          <Link to={`/prioritization/sessions/${setBySession.id}`} className="underline hover:text-[var(--text-pri)]">
            {setBySession.name.trim() || 'Untitled session'}
          </Link>
          . Change it there.
        </p>
      )}
    </div>
  );
}
